import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi'; // Using react-icons for the mail icon
import { useFormik } from 'formik';
import { useAuthStore, useOnboardingStore } from '../../store';
import { loginAdminSchema } from '../../constants/schemas';

import Tabs from '../../components/base/tabs';
import AppInput from '../../components/base/input';
import Arrow from '../../assets/images/auth/login-arrow.png';
import PhoneNumberInput from '../../components/base/phone-input-tel';
import AppButton from '../../components/base/button';
import BaseImage from '../../components/base/base-image';
import AppCheckbox from '../../components/base/Checkbox';
import AuthFooter from '../../layout/component/auth-footer';
import toast from 'react-hot-toast';
import { OnboardingKybStatusEnum } from '../../enums/onboarding.enum';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);

  const [currentTab, setCurrentTab] = useState('Email address');
  const handleTabChange = (tab: string) => {
    setCurrentTab(tab);
  };

  const [phone, setPhone] = useState('');
  const handlePhoneChange = (value: string) => {
    setPhone(value);
  };

  const { login, addKybStatusToLocalStorage, loading } = useAuthStore();
  const { getKybStatus } = useOnboardingStore();
  const formik = useFormik({
    initialValues: {
      password: '',
      email: '',
    },
    validationSchema: loginAdminSchema,
    validateOnMount: true,
    async onSubmit(values) {
      const payload = {
        emailOrMobile: currentTab === 'Email address' ? values.email.trim() : phone.trim(),
        password: values.password,
      };

      const response = await login(payload);
      if (response && response.isSuccess) {
        const kybStatusResp = await getKybStatus();
        addKybStatusToLocalStorage(
          kybStatusResp && kybStatusResp.isSuccess
            ? kybStatusResp.value[0].verificationStatus
            : 0,
        );
        if (
          kybStatusResp.value[0].verificationStatus ===
          OnboardingKybStatusEnum.Rejected
        ) {
          navigate('/kyb-failed');
        } else if (
          kybStatusResp.value[0].verificationStatus ===
          OnboardingKybStatusEnum.Approved
        ) {
          navigate('/dashboard');
        } else {
          navigate('/start-kyb');
        }
        toast.success('Login Successful');
      }
    },
  });

  const routeToSignUp = () => {
    navigate('/signup');
  };
  const routeToForgotPassword = () => {
    navigate('/forgot-password');
  };
  return (
    <div className="w-[100%] 2xl:w-[70%] md:w-[80%] p-6 mx-auto mb-4 flex flex-col justify-center md:min-h-screen relative animate__animated animate__slideInRight">
      <BaseImage imgSrc={Arrow} imgAlt="Arrow" imgClass="2xl:mt-12 w-24" />

      <div className="p-12 pt-8">
        <p className="font-bold mb-8 text-2xl">Login to your account</p>
        <div className="mb-4">
        <Tabs
          tabs={[
            'Email address',
            'Phone number',
          ]}
          activeTab={currentTab}
          onTabChange={handleTabChange}
        />
        </div>
        <form onSubmit={formik.handleSubmit} action="#">
          {currentTab === 'Email address' ? (
            <div className="mb-5">
              <AppInput
                type="text"
                name="email"
                id="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                placeholder="Enter your email address"
                required
                label="Email address"
                errorMessage={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : ''
                }
              />
            </div>
          ) : (
            <div className="mb-5">
              <PhoneNumberInput
                label="Phone number"
                phone={phone}
                onPhoneChange={handlePhoneChange}
                required
              />
            </div>
          )}

          <div className="w-full mb-6">
            <AppInput
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
              placeholder="Enter your password"
              required
              label="Password"
              endIcon={
                <button
                  type="button"
                  className={`absolute px-4 right-0`}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEye /> : <FiEyeOff />}
                </button>
              }
              errorMessage={
                formik.touched.password && formik.errors.password
                  ? formik.errors.password
                  : ''
              }
            />
          </div>

          <div className="flex justify-between items-center mb-6 cursor-pointer">
            <AppCheckbox label="Remember Me" />
            <p
              className="text-sm text-gray-500"
              onClick={routeToForgotPassword}
            >
              Forgot Password?
            </p>
          </div>

          <AppButton
            label="Login"
            submitType="submit"
            type="dark"
            loading={loading}
            disabled={loading || !formik.isValid}
          />
        </form>

        <p className="mt-10 text-center text-sm">
          New here?{' '}
          <span
            onClick={routeToSignUp}
            className="text-primary font-bold cursor-pointer inline-block ml-1"
          >
            Create Account
          </span>
        </p>
      </div>

      <AuthFooter />
    </div>
  );
};

export default Login;
